import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { 
  ArrowLeft, 
  Mail, 
  Users, 
  Shield, 
  Clock, 
  CheckCircle,
  Loader2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const EarlyAccess = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [waitlistCount, setWaitlistCount] = useState(0);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    // Load waitlist count
    const loadCount = async () => {
      try {
        const response = await api.get('/waitlist/count');
        setWaitlistCount(response.data.count);
      } catch (error) {
        console.error('Failed to load waitlist count');
      }
    };
    loadCount();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check if it's a Gmail address
    if (!email.toLowerCase().includes('gmail.com') && !email.toLowerCase().includes('googlemail.com')) {
      toast.error('Please use a Gmail address for early access');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/waitlist/join', { email });
      setIsSubmitted(true);
      setPosition(response.data.position);
      setWaitlistCount(prev => prev + 1);
      toast.success('You\'re on the list!');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('You\'re already on the waitlist!');
        setIsSubmitted(true);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to join waitlist');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_96339eda-de9f-4afd-b0cc-aa940ad2aaf2/artifacts/doc4booq_SubGuard_icon.png" 
                  alt="SubGuard" 
                  className="w-8 h-8 rounded-lg"
                />
                <span className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  SubGuard
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D1FAE5] text-[#064E3B] rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Early Access
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Outfit' }}>
            Join the Waitlist
          </h1>
          <p className="text-lg text-gray-600 max-w-lg mx-auto">
            We're putting the finishing touches on SubGuard. Join our early access list to be one of the first to try it.
          </p>
        </div>

        {/* Counter */}
        <div className="flex justify-center mb-10">
          <Card className="bg-white border-0 shadow-lg">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-14 h-14 bg-[#D1FAE5] rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-[#064E3B]" />
              </div>
              <div>
                <p className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
                  {waitlistCount}
                </p>
                <p className="text-sm text-gray-500">people on the waitlist</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Signup Form or Success */}
        {!isSubmitted ? (
          <Card className="bg-white border-0 shadow-xl mb-12">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gmail Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="email"
                      placeholder="you@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-12 h-14 text-lg rounded-xl border-gray-200 focus:border-[#064E3B] focus:ring-[#064E3B]"
                      data-testid="waitlist-email"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    * Gmail address required for our email scanning feature
                  </p>
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full h-14 text-lg bg-[#064E3B] hover:bg-[#053D2E] text-white rounded-xl"
                  data-testid="join-waitlist-btn"
                >
                  {isSubmitting ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Joining...</>
                  ) : (
                    'Join the Waitlist'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#D1FAE5] border-0 shadow-xl mb-12">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 bg-[#064E3B] rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-[#064E3B] mb-2" style={{ fontFamily: 'Outfit' }}>
                You're on the list!
              </h2>
              {position && (
                <p className="text-[#064E3B] font-medium mb-2">
                  You're #{position} on the waitlist
                </p>
              )}
              <p className="text-[#064E3B]/80">
                We'll email you as soon as we can add you to SubGuard. Thanks for your patience!
              </p>
            </CardContent>
          </Card>
        )}

        {/* Why Waitlist */}
        <div className="mb-12">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center" style={{ fontFamily: 'Outfit' }}>
            Why a waitlist?
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="bg-white border border-gray-100">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Google Verification</h3>
                <p className="text-sm text-gray-600">
                  We're going through Google's security verification process to ensure your data is handled safely. This takes a few weeks.
                </p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-gray-100">
              <CardContent className="p-6">
                <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Limited Early Access</h3>
                <p className="text-sm text-gray-600">
                  While verification is pending, we're manually adding early adopters. Join the list and we'll add you as soon as we can.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* What to Expect */}
        <div className="bg-white rounded-2xl p-8 border border-gray-100">
          <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Outfit' }}>
            What happens next?
          </h2>
          <ol className="space-y-4">
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-[#D1FAE5] text-[#064E3B] rounded-full flex items-center justify-center font-bold flex-shrink-0">1</span>
              <div>
                <p className="font-medium text-gray-900">We add you to our system</p>
                <p className="text-sm text-gray-600">We'll manually add your Gmail to our approved testers list</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-[#D1FAE5] text-[#064E3B] rounded-full flex items-center justify-center font-bold flex-shrink-0">2</span>
              <div>
                <p className="font-medium text-gray-900">You get an email</p>
                <p className="text-sm text-gray-600">We'll notify you when your account is ready</p>
              </div>
            </li>
            <li className="flex gap-4">
              <span className="w-8 h-8 bg-[#D1FAE5] text-[#064E3B] rounded-full flex items-center justify-center font-bold flex-shrink-0">3</span>
              <div>
                <p className="font-medium text-gray-900">Start saving money</p>
                <p className="text-sm text-gray-600">Connect your Gmail, find subscriptions, and cancel what you don't need</p>
              </div>
            </li>
          </ol>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-6 mb-4">
            <a href="/privacy" className="text-sm hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="text-sm hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
          <p className="text-sm">© {new Date().getFullYear()} SubGuard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default EarlyAccess;
